'use client'

import clsx from 'clsx'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { useMemo, useState } from 'react'

import {
  AppointmentDate,
  SelectedSchedule,
} from '@/features/common-requests/call-arrangement-context'
import {
  PrescriptionQuizFooter,
  PrescriptionQuizHeader,
  prescriptionQuestionItemMotion,
  prescriptionQuestionListMotion,
  prescriptionStepMotion,
} from '@/features/prescriptions/request/PrescriptionQuizShell'

type Props = {
  availableDates: Array<AppointmentDate>
  selectedSchedule: SelectedSchedule | null
  immediateSlotAvailable: boolean
  onSelectSchedule: (selected: SelectedSchedule | null) => void
  onBack?: () => void
  onNext: () => void
  currentStep?: number
  totalSteps?: number
  serviceLabel: string
  mobileServiceLabel?: string
  nextLabel?: string
  nextLoading?: boolean
  /** Replaces the step counter, e.g. the booking reference on the reschedule flow. */
  metaLabel?: string
  /** Hides Back on the first view for flows that start here. */
  backHiddenOnChoice?: boolean
  /** Label used when the action submits instead of advancing to another step. */
  submitLabel?: string
  /** Rendered under the footer, e.g. a submission error. */
  footerNote?: React.ReactNode
  /** Overrides the first screen's copy, e.g. to acknowledge a missed call. */
  choiceTitle?: string
  choiceDescription?: string
}

export default function QuizCallArrangement({
  availableDates,
  selectedSchedule,
  immediateSlotAvailable,
  onSelectSchedule,
  onBack,
  onNext,
  currentStep,
  totalSteps,
  serviceLabel,
  mobileServiceLabel,
  nextLabel,
  nextLoading = false,
  metaLabel,
  backHiddenOnChoice = false,
  submitLabel,
  footerNote,
  choiceTitle = 'When should the doctor call?',
  choiceDescription = "The call comes from +61 3 9007 2145 — save the number so you don't miss it.",
}: Props) {
  const reduceMotion = useReducedMotion()
  const [view, setView] = useState<'choice' | 'slots'>('choice')
  const [scheduleChoice, setScheduleChoice] = useState<
    SelectedSchedule['type'] | null
  >(selectedSchedule?.type ?? null)
  const initialDay =
    selectedSchedule?.type === 'scheduled'
      ? selectedSchedule.selected.day
      : availableDates[0]?.day
  const [selectedDay, setSelectedDay] = useState(initialDay)

  const selectedDate = useMemo(
    () => availableDates.find((date) => date.day === selectedDay),
    [availableDates, selectedDay],
  )

  function selectDay(day: string) {
    setSelectedDay(day)
    if (
      selectedSchedule?.type === 'scheduled' &&
      selectedSchedule.selected.day !== day
    )
      onSelectSchedule(null)
  }

  function selectScheduleChoice(choice: SelectedSchedule['type']) {
    setScheduleChoice(choice)
    if (choice === 'immediate') onSelectSchedule({ type: 'immediate' })
    else if (selectedSchedule?.type === 'immediate') onSelectSchedule(null)
  }

  function continueFromCurrentView() {
    if (view === 'choice' && scheduleChoice === 'scheduled') {
      setView('slots')
      return
    }
    onNext()
  }

  // On the choice view, picking "Pick a time" advances to the slots view; every
  // other combination runs the flow's own action, so it may need a submit label.
  const advancesToAnotherView =
    view === 'choice' && scheduleChoice !== 'immediate'
  const resolvedNextLabel =
    !advancesToAnotherView && submitLabel ? submitLabel : nextLabel

  return (
    <AnimatePresence initial={!reduceMotion} mode="wait">
      <motion.div
        animate="animate"
        exit={reduceMotion ? undefined : 'exit'}
        initial={reduceMotion ? false : 'initial'}
        key={view}
        variants={prescriptionStepMotion}
      >
        <PrescriptionQuizHeader
          currentStep={currentStep}
          description={
            view === 'choice'
              ? choiceDescription
              : 'The doctor calls within 2 hours of the slot you choose — from +61 3 9007 2145.'
          }
          metaLabel={metaLabel}
          mobileServiceLabel={mobileServiceLabel}
          serviceLabel={serviceLabel}
          title={view === 'choice' ? choiceTitle : 'Pick a time for the call.'}
          totalSteps={totalSteps}
        />

        {view === 'choice' ? (
          <motion.div
            animate="animate"
            className="flex flex-col gap-3 sm:gap-4"
            initial="initial"
            variants={prescriptionQuestionListMotion}
          >
            <motion.div variants={prescriptionQuestionItemMotion}>
              <ScheduleChoice
                description="The doctor calls within the next 2 hours."
                disabled={!immediateSlotAvailable}
                label="As soon as possible"
                onClick={() => selectScheduleChoice('immediate')}
                selected={scheduleChoice === 'immediate'}
              />
              {!immediateSlotAvailable && (
                <p className="mt-2.5 flex items-start gap-2 text-[13px] leading-[18px] text-[#5C5C64]">
                  <InfoIcon />
                  We’re closed right now — doctors call between 6am and
                  midnight. Pick a time below instead.
                </p>
              )}
            </motion.div>
            <motion.div variants={prescriptionQuestionItemMotion}>
              <ScheduleChoice
                description="Choose a day and a 30-minute slot over the next 3 days."
                label="Pick a time"
                onClick={() => selectScheduleChoice('scheduled')}
                selected={scheduleChoice === 'scheduled'}
              />
            </motion.div>
          </motion.div>
        ) : (
          <motion.div
            animate="animate"
            className="flex flex-col"
            initial="initial"
            variants={prescriptionQuestionListMotion}
          >
            <motion.div
              className="mt-4 flex flex-col gap-3.5 sm:mt-[22px]"
              variants={prescriptionQuestionItemMotion}
            >
              <h2 className="text-[15px] font-semibold leading-5 text-[#0B0B0F] sm:text-[16px]">
                Which day?
              </h2>
              <div className="grid grid-cols-3 gap-2.5 sm:gap-4">
                {availableDates.map((date, index) => (
                  <button
                    className={clsx(
                      'rounded-[14px] border-[1.5px] bg-[#F4F4F6] px-2 py-3 text-center transition-[border-color,background-color] duration-300',
                      selectedDay === date.day
                        ? 'border-[#0B0B0F]'
                        : 'border-transparent hover:bg-[#EFEFF2]',
                    )}
                    key={`${date.day}-${date.date}`}
                    onClick={() => selectDay(date.day)}
                    type="button"
                  >
                    <span
                      className={clsx(
                        'block text-[13px] leading-[18px] sm:text-[14px] sm:leading-5',
                        selectedDay === date.day
                          ? 'font-semibold text-[#0B0B0F]'
                          : 'font-medium text-[#5C5C64]',
                      )}
                    >
                      {getDayLabel(date.day, index)}
                    </span>
                    <span className="block text-[11.5px] leading-4 text-[#82828B] sm:text-[12.5px] sm:leading-[18px]">
                      {formatDateLabel(date.day, date.date)}
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>

            {selectedDate && selectedDate.availableTimes.length > 0 ? (
              <motion.div
                className="mt-5 flex flex-col gap-3.5"
                variants={prescriptionQuestionItemMotion}
              >
                <h2 className="text-[15px] font-semibold leading-5 text-[#0B0B0F] sm:text-[16px]">
                  Pick a time
                </h2>
                <div className="grid grid-cols-3 gap-2.5 sm:grid-cols-4 sm:gap-3">
                  {selectedDate.availableTimes.map((slot) => {
                    const selected =
                      selectedSchedule?.type === 'scheduled' &&
                      selectedSchedule.selected.millis === slot.millis
                    return (
                      <button
                        className={clsx(
                          'h-[48px] rounded-[14px] border-[1.5px] bg-[#F4F4F6] text-[14px] font-medium transition-[border-color,background-color] duration-300',
                          selected
                            ? 'border-[#0B0B0F] text-[#0B0B0F]'
                            : 'border-transparent text-[#5C5C64] hover:bg-[#EFEFF2]',
                        )}
                        key={slot.millis}
                        onClick={() =>
                          onSelectSchedule({
                            type: 'scheduled',
                            selected: { ...selectedDate, ...slot },
                          })
                        }
                        type="button"
                      >
                        {slot.time}
                      </button>
                    )
                  })}
                </div>
              </motion.div>
            ) : selectedDate ? (
              <motion.div
                className="flex flex-col items-center gap-[9px] pb-6 pt-10 text-center sm:gap-2.5 sm:pb-[30px] sm:pt-12"
                variants={prescriptionQuestionItemMotion}
              >
                <ClockIcon />
                <p className="text-[14.5px] font-semibold leading-5 text-[#0B0B0F] sm:text-[15.5px]">
                  {selectedDay === availableDates[0]?.day
                    ? 'No more slots today'
                    : `No more slots ${getDayLabel(
                        selectedDate.day,
                        availableDates.findIndex(
                          (date) => date.day === selectedDate.day,
                        ),
                      ).toLowerCase()}`}
                </p>
                <p className="max-w-[290px] text-[13px] leading-[18px] text-[#82828B] sm:max-w-[420px] sm:text-[13.5px] sm:leading-[19px]">
                  {selectedDay === availableDates[0]?.day
                    ? "We've finished calling for today — doctors call between 6am and midnight. Tomorrow still has plenty of slots."
                    : 'There are no appointments left on this day. Choose another day to see the available times.'}
                </p>
              </motion.div>
            ) : null}
          </motion.div>
        )}

        <PrescriptionQuizFooter
          backDisabled={nextLoading}
          backHidden={backHiddenOnChoice && view === 'choice'}
          nextArrowHidden={Boolean(submitLabel) && !advancesToAnotherView}
          nextDisabled={
            nextLoading ||
            (view === 'choice'
              ? !scheduleChoice
              : selectedSchedule?.type !== 'scheduled')
          }
          nextLabel={resolvedNextLabel}
          nextLoading={nextLoading}
          onBack={view === 'choice' ? onBack : () => setView('choice')}
          onNext={continueFromCurrentView}
        />
        {footerNote}
      </motion.div>
    </AnimatePresence>
  )
}

function ScheduleChoice({
  label,
  description,
  selected,
  disabled = false,
  onClick,
}: {
  label: string
  description: string
  selected: boolean
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      aria-checked={selected}
      className={clsx(
        'flex min-h-[72px] w-full items-center gap-4 rounded-[16px] border-[1.5px] bg-[#F4F4F6] px-4 py-3.5 text-left transition-[border-color,background-color,opacity] duration-300 sm:px-5',
        selected
          ? 'border-[#0B0B0F]'
          : 'border-transparent enabled:hover:bg-[#EFEFF2]',
        disabled && 'cursor-not-allowed opacity-45',
      )}
      disabled={disabled}
      onClick={onClick}
      role="radio"
      type="button"
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-semibold leading-5">
          {label}
        </span>
        <span className="mt-1 block text-[13px] leading-[18px] text-[#82828B]">
          {description}
        </span>
      </span>
      <span
        className={clsx(
          'flex h-5 w-5 shrink-0 items-center justify-center rounded-full border-[1.5px]',
          selected
            ? 'border-[#0B0B0F] bg-[#0B0B0F]'
            : 'border-[#C4C4CA] bg-white',
        )}
      >
        {selected && <span className="h-2 w-2 rounded-full bg-white" />}
      </span>
    </button>
  )
}

function InfoIcon() {
  return (
    <span className="mt-0.5 flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-full border border-current text-[9px] leading-none">
      i
    </span>
  )
}

const WEEKDAY_LABELS: Record<string, string> = {
  Mon: 'Monday',
  Tue: 'Tuesday',
  Wed: 'Wednesday',
  Thu: 'Thursday',
  Fri: 'Friday',
  Sat: 'Saturday',
  Sun: 'Sunday',
}

const MONTH_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
]

function getDayLabel(day: string, index: number) {
  if (index === 0) return 'Today'
  if (index === 1) return 'Tomorrow'
  return WEEKDAY_LABELS[day] ?? day
}

function formatDateLabel(day: string, date: string) {
  const [dayOfMonth, month] = date.split('/').map(Number)
  const monthLabel = MONTH_LABELS[month - 1]
  if (!dayOfMonth || !monthLabel) return date
  return `${day} ${dayOfMonth} ${monthLabel}`
}

function ClockIcon() {
  return (
    <svg
      aria-hidden="true"
      className="h-[26px] w-[26px] text-[#A0A0A8] sm:h-7 sm:w-7"
      fill="none"
      viewBox="0 0 28 28"
    >
      <circle cx="14" cy="14" r="11" stroke="currentColor" strokeWidth="1.7" />
      <path
        d="M14 8.5V14L17.6 16.1"
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.7"
      />
    </svg>
  )
}
